import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { platformApi, type PlatformFlag, type TenantDetail } from '../../../lib/api/platform.api';

/** Los interruptores que solo cambia la plataforma: si el seminario es de
 *  demostración, y cada función registrada en `FEATURE_FLAGS`. */
export function TenantFlagsSection({
  tenantId,
  flags,
  onChanged,
}: {
  tenantId: string;
  flags: TenantDetail['flags'];
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation('platform');
  const [saving, setSaving] = useState<PlatformFlag | null>(null);
  const [error, setError] = useState(false);
  const [confirmDemoOff, setConfirmDemoOff] = useState(false);

  async function set(flag: PlatformFlag, enabled: boolean) {
    setSaving(flag);
    setError(false);
    try {
      await platformApi.setTenantPlatformFlag(tenantId, flag, enabled);
      await onChanged();
    } catch {
      setError(true);
    } finally {
      setSaving(null);
    }
  }

  const platform = flags.features.filter((f) => f.scope === 'platform');
  const tenant = flags.features.filter((f) => f.scope === 'tenant');

  const row = (f: TenantDetail['flags']['features'][number]) => (
    <FormControlLabel
      key={f.key}
      control={
        <Switch
          size="small"
          checked={f.enabled}
          disabled={saving !== null}
          onChange={(e) => void set(f.key, e.target.checked)}
        />
      }
      label={
        <Typography variant="body2">
          {t(`flags.features.${f.key}`, { defaultValue: f.key })}
          {!f.explicit && (
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              · {t('flags.byDefault')}
            </Typography>
          )}
        </Typography>
      }
    />
  );

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
        {t('flags.title')}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        {t('flags.hint')}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {t('flags.saveError')}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={flags.isDemo}
            disabled={saving !== null}
            onChange={(e) =>
              e.target.checked ? void set('isDemo', true) : setConfirmDemoOff(true)
            }
          />
        }
        label={
          <Typography variant="body2" fontWeight={600}>
            {t('flags.demo')}
          </Typography>
        }
      />
      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        {t('flags.demoHint')}
      </Typography>

      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
        {t('flags.platformGroup')}
      </Typography>
      {platform.length === 0 ? (
        <Typography variant="caption" color="text.disabled" display="block" mb={2}>
          {t('flags.platformEmpty')}
        </Typography>
      ) : (
        <Stack mb={2}>{platform.map(row)}</Stack>
      )}

      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
        {t('flags.tenantGroup')}
      </Typography>
      <Typography variant="caption" color="text.disabled" display="block">
        {t('flags.tenantGroupHint')}
      </Typography>
      <Stack>{tenant.map(row)}</Stack>

      <Dialog open={confirmDemoOff} onClose={() => setConfirmDemoOff(false)}>
        <DialogTitle>{t('flags.demoOffTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('flags.demoOffBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDemoOff(false)}>{t('flags.cancel')}</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              setConfirmDemoOff(false);
              void set('isDemo', false);
            }}
          >
            {t('flags.demoOffConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
